import { Autocomplete } from "@mui/material";
import BootstrapTextField from "Common/BootstrapTextField";
import { autocompleteDropdownStyles } from "Constants/Style";
import { InwardType } from "Constants/inwardTypes";
import { Axios } from "api/axios";
import CancelBackButton from "components/Buttons/CancelBackButton";
import SubmitButton from "components/Buttons/SubmitButton";
import FormHeader from "components/Form&DetailsHeader/Form&DetailsHeader";
import LoadingSpinner from "components/LoaderErrorWrapper/LoadingSpinner";
import PageContainer from "components/PageContainer/PageContainer";
import { useFormik } from "formik";
import React, { useCallback, useEffect, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { fetchLabourDropdownForForms } from "slices/Labour/thunk";
import { fetchWarehouseDropdown } from "slices/Master/Warehouse/thunk";
import { resetGRNState } from "slices/Warehouse/ItemInward2/GRN/reducer";
import { fetchGRNDetails } from "slices/Warehouse/ItemInward2/GRN/thunk";
import { clearPendingLabourInwardDetails } from "slices/Warehouse/ItemInward2/PendingLabourInward/reducer";
import { updatePendingLabourInward } from "slices/Warehouse/ItemInward2/PendingLabourInward/thunk";
import { fetchSupplierBydropdown } from "slices/suppliers/thunk";
import { showConfirm, showError, showValidationErrors } from "utils/swalHelper";
import * as Yup from "yup";
import ConfigureItems from "../ConfigureItemsTables/ConfigureItems";
import LabourReturnedItems from "../ConfigureItemsTables/LabourReturnedItems";
import { useAutoMinimizeSidebar } from "CustomHooks/useAutoMinimizeSidebar";

interface GRNFormValues {
  inventory_id: string;
  dispatch_id: string;
  dispatch_number: string;
  inward_date: string;
  labour_id: string;
  labour_name: string;
  upload_document: File | null;
  existing_document_file: string | null; // Store existing document URL for edit mode
  warehouse_id: string | null;
  warehouse_name: string | null;
  remark: string;
  received_by: string;
  received_by_user_id: string;
}

const EditLabourInwardGNR: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams<{ id?: string }>();
  const dispatch = useDispatch<any>();
  const { details: grnData, loading } = useSelector((s: any) => s.grn);

  // Check if we're in edit mode (ID in URL params)
  const inventoryId = params.id;
  const isEdit = Boolean(inventoryId);
  const [grnItems, setGrnItems] = useState<any[]>([]);
  const [labourOptions, setLabourOptions] = useState<any[]>([]);
  const [labourItems, setLabourItems] = useState<any[]>([]);
  const [userOptions, setUserOptions] = useState<any[]>([]);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [supplierOptions, setSupplierOptions] = useState<any[]>([]);
  const [warehouseOptions, setWarehouseOptions] = useState<any[]>([]);
  const [isLoadingInitialData, setIsLoadingInitialData] = useState(false);

   // Auto-minimize sidebar on tablet screens
   useAutoMinimizeSidebar();

  // Labour options
  useEffect(() => {
    const fetchLabourOptions = async () => {
      try {
        const response = await dispatch(fetchLabourDropdownForForms()).unwrap();
        setLabourOptions(response || []);
      } catch (error) {
        console.error("Error fetching supplier options:", error);
      }
    };
    fetchLabourOptions();
  }, [dispatch]);

  useEffect(() => {
    const fetchSupplierOptions = async () => {
      try {
        const response = await dispatch(fetchSupplierBydropdown()).unwrap();
        setSupplierOptions(response?.data || []);
      } catch (error) {
        console.error("Error fetching supplier options:", error);
      }
    };
    fetchSupplierOptions();
  }, [dispatch]);

  useEffect(() => {
    const fetchWarehouseOptions = async () => {
      try {
        const response = await dispatch(fetchWarehouseDropdown()).unwrap();
        setWarehouseOptions(response?.data || []);
      } catch (error) {
        console.error("Error fetching warehouse options:", error);
      }
    };
    fetchWarehouseOptions();
  }, [dispatch]);

  useEffect(() => {
    const fetchUserOptions = async () => {
      try {
        const response = await Axios.get("/users/dropdown/all");
        // console.log("User Options:", response);
        setUserOptions(response?.data?.data || []);
      } catch (error) {
        console.error("Error fetching user options:", error);
        setUserOptions([]);
      }
    };
    fetchUserOptions();
  }, [dispatch]);

  useEffect(() => {
    setLabourItems([]);
    if (inventoryId) {
      dispatch(fetchGRNDetails(inventoryId));
    }
  }, [inventoryId]);

  useEffect(() => {
    return () => {
      dispatch(resetGRNState());
    };
  }, [dispatch]);

  // Fetch GRN data
  useEffect(() => {
    if (!grnData || initialDataLoaded) return;

    console.log("GRN DATA 👍 =>", grnData);
    formik.setFieldValue("dispatch_id", grnData?.dispatch?.dispatch_id || "");
    formik.setFieldValue("inventory_id", grnData?.inventory_id || "");
    formik.setFieldValue(
      "dispatch_number",
      grnData?.dispatch?.dispatch_number || ""
    );
    formik.setFieldValue(
      "inward_date",
      grnData.inward_date
        ? new Date(grnData.inward_date).toISOString().split("T")[0]
        : ""
    );
    formik.setFieldValue("remark", grnData.remarks || "");

    //  Labour details add here for edit
    const labourData =
      grnData.dispatch || grnData.labour_details || grnData.labour;
    formik.setFieldValue("labour_id", labourData?.labour_id || "");
    formik.setFieldValue("labour_name", labourData?.labour_name || "");

    //  Labour details add here for edit
    const receivedData =
      grnData.received_by_details || grnData.received_by || grnData;
    const fullName = `${receivedData?.first_name} ${receivedData?.last_name}`;
    formik.setFieldValue("received_by", fullName || "");
    formik.setFieldValue("received_by_user_id", receivedData?.user_id || "");
    formik.setFieldValue(
      "existing_document_file",
      grnData.document_file || null
    );

    //  Labour details add here for edit
    const warehouseData = grnData.warehouse_details || grnData.warehouse;
    formik.setFieldValue("warehouse_id", warehouseData?.warehouse_id || null);
    formik.setFieldValue(
      "warehouse_name",
      warehouseData?.warehouse_name || null
    );
    formik.setFieldValue("inward_type", "labour_inward");

    if (grnData.dispatch_items) {
      setLabourItems(grnData?.dispatch_items);
    }

    // Transform inventory_items to RowData format
    // Transform inventory_items to RowData format (only item_type = "new")
    if (grnData.inventory_items && grnData.inventory_items.length > 0) {
      const transformedItems = grnData.inventory_items
        .filter((invItem: any) => invItem.item_type === "new")
        .map((invItem: any, index: any) => {
          const quantity = Number(invItem.quantity) || 0;
          const rate = Number(invItem.selling_price) || 0;

          return {
            id: `edit-${invItem.inventory_item_id}-${index}`,
            inventory_item_id: invItem.inventory_item_id,
            sr_no: invItem.sr_no,
            item_type: invItem.item_type, // ✅ dynamic
            bag_no: invItem.bag_no || "",
            quantity: invItem.quantity,
            return_quantity: invItem.quantity,
            received_quantity: quantity,
            item_id: invItem.master_item?.item_id,
            itemName: invItem.master_item?.item_name,
            itemCode: invItem.master_item?.item_code,
            remark: invItem.remark || "",
            master_item: invItem.master_item,
            category: invItem.master_item?.category?.category_name,
            category_id: invItem.master_item?.category?.category_id,
            subCategory: invItem.master_item?.sub_category?.sub_category_name,
            sub_category_id: invItem.master_item?.sub_category?.sub_category_id,
            unit: invItem.master_item?.unit?.unit_name || "",
            rate: invItem.selling_price,
            amount: quantity * rate,
          };
        });

      console.log("Initial transformedItems => ", transformedItems);
      setGrnItems(transformedItems);
    }

    if (grnData.inventory_items && grnData.inventory_items.length > 0) {
      const returnedItems = grnData.inventory_items
        .filter((invItem: any) => invItem.item_type === "returned")
        .map((invItem: any) => {
          return {
            inventory_item_id: invItem.inventory_item_id,
            sr_no: invItem.sr_no,
            item_type: invItem.item_type, // ✅ dynamic
            bag_no: invItem.bag_no || "-",
            dispatch_item_id: invItem.inventory_item_id,
            quantity: invItem.quantity,
            return_quantity: invItem.quantity,
            master_item: invItem.master_item,

            item_id: invItem.master_item?.item_id,
            item_name: invItem.master_item?.item_name,
            item_code: invItem.master_item?.item_code,

            category_name: invItem.master_item?.category?.category_name,
            category_id: invItem.master_item?.category?.category_id,

            sub_category_name:
              invItem.master_item?.sub_category?.sub_category_name,
            sub_category_id: invItem.master_item?.sub_category?.sub_category_id,

            unit: invItem.master_item?.unit?.unit_name,
            unit_id: invItem.master_item?.unit?.unit_id,

            dispatch_quantity: Number(invItem.issued_quantity) || 0,
            issued_quantity: Number(invItem.issued_quantity) || 0,
            // return_quantity: Number(invItem.return_quantity) || 0,

            remark: invItem.remark || "",
          };
        });

      console.log("Initial returnItems => ", returnedItems);
      setLabourItems(returnedItems);
    }

    setInitialDataLoaded(true);
  }, [grnData, isEdit, dispatch]);
  // }, [isEdit, id, dispatch, navigate, initialDataLoaded, formik]);

  // Get today's date in YYYY-MM-DD format for date input
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  const validationSchema = Yup.object({
    // grn_no: Yup.string().required("Please enter GRN No."),
    dispatch_number: Yup.string().required("Please enter Dispatch number"),
    inward_date: Yup.string().required("Please select Inward Date"),
    labour_name: Yup.string().required("Please enter Labour name"),
    received_by: Yup.string().required("Please select Received By"),
    remark: Yup.string(),
    upload_document: Yup.mixed()
      .nullable()
      .test("fileSize", "File size too large (max 2MB)", (file: any) => {
        if (!file) return true; // skip if empty
        return file.size <= 2 * 1024 * 1024;
      }),
  });

  const formik = useFormik<GRNFormValues>({
    initialValues: {
      inventory_id: "",
      dispatch_id: "",
      dispatch_number: "",
      inward_date: isEdit ? "" : getTodayDate(), // Auto-select today's date in add mode
      labour_name: "",
      labour_id: "",
      upload_document: null as File | null,
      existing_document_file: null as string | null,
      warehouse_id: "",
      warehouse_name: "",
      remark: "",
      received_by: "",
      received_by_user_id: "",
    },

    validationSchema: validationSchema,
    onSubmit: async (values) => {
      // ✅ Validate items
      if (grnItems.length === 0) {
        showError("Please add at least one item");
        return;
      }

      // ✅ Validate quantity for gnrdirect type
      // const invalidQuantity = grnItems.some((item, index) => {
      //   if (
      //     item.quantity === undefined ||
      //     item.quantity === null ||
      //     item.quantity <= 0
      //   ) {
      //     showError(`Quantity must be greater than 0 for row ${index + 1}`);
      //     return true;
      //   }
      //   return false;
      // });

      // if (invalidQuantity) return;

      // ✅ Validate rate for items that require rate (not LABOUR_INWARD)
      // const invalidRate = grnItems.some((item, index) => {
      //   if (item.rate === undefined || item.rate === null || item.rate <= 0) {
      //     showError(`Rate must be greater than 0 for row ${index + 1}`);
      //     return true;
      //   }
      //   return false;
      // });

      // if (invalidRate) return;

      // ✅ Build FormData
      console.log("Values =>", values);
      console.log("grnItems from component =>", grnItems);
      console.log("labourItems from component =>", labourItems);
      const formData = buildFormData(values, grnItems, labourItems);

      console.log("------------------------------------------");
      formData.forEach((key, val) => {
        console.log(`${key} => ${val}`);
      });
      console.log("------------------------------------------");

      try {
        if (inventoryId) {
          console.log("From submit", inventoryId);
          await dispatch(
            updatePendingLabourInward({ inventory_id: inventoryId, formData })
          ).unwrap();
        }
        toast.success("GRN Edited successfully");
        navigate("/warehouse-item-inward");
      } catch (err: any) {
        handleSubmitError(err);
      }
    },
  });

  // Helper function to build FormData
  const buildFormData = useCallback(
    (values: GRNFormValues, items: any[], returnItems: any[]): FormData => {
      const formData = new FormData();
      formData.append("inventory_id", values.inventory_id);
      formData.append("dispatch_id", values.dispatch_id);
      formData.append("dispatch_number", values.dispatch_number);
      formData.append("inward_date", values.inward_date);
      formData.append("labour_id", values.labour_id || "");
      formData.append("labour_name", values.labour_name || "");
      formData.append("remark", values.remark || "");
      formData.append("warehouse_id", values.warehouse_id || "");
      formData.append("warehouse_name", values.warehouse_name || "");
      formData.append("received_by", values.received_by_user_id || "");
      // formData.append("received_by_user_id", values.received_by_user_id || "");
      formData.append("inward_type", InwardType.LABOUR_INWARD);

      // Append file if it exists (must be File object, not string)
      if (values.upload_document && values.upload_document instanceof File) {
        formData.append("document_file", values.upload_document);
        // formData.append("document", values.upload_document);
      }

      // Transform RowData to GRNItem format
      const transformedItems = items.map((row: any) => {
        // console.log("When submit transformed Items", row);

        const grnItem: any = {
          // --- ALREADY PRESENT FIELDS ---
          item_type: row.item_type,
          item_id: row.item_id,
          item_name: row.itemName,
          item_code: row.itemCode,
          category_id: row.category_id,
          category_name: row.category,
          sub_category_id: row.sub_category_id,
          sub_category_name: row.subCategory,
          remark: row.remarks || null,
          quantity: row.received_quantity,

          // --- NEWLY ADDED FIELDS FROM EDIT MODE ---
          inventory_item_id: row.inventory_item_id, // <-- added
          sr_no: row.sr_no, // <-- added
          bag_no: row.bag_no, // <-- added
          rate: row.rate || null, // <-- added
          amount: row.amount || 0, // <-- added
          received_quantity: row.received_quantity, // <-- added (raw received qty)
          unit_name: row.unit, // <-- added
          id: row.id, // <-- added
        };

        const quantity = Number(row.received_quantity) || 0;
        grnItem.grn_items_details = [
          {
            quantity: quantity,
            selling_price: row.rate ? Number(row.rate) : null,
            remark: row.remarks || null,
          },
        ];

        return grnItem;
      });

      const transformedReturnItems = returnItems.map((row: any) => {
        return {
          // --- EXISTING FIELDS ---
          item_type: row.item_type, // "returned"
          dispatch_item_id: row.dispatch_item_id,
          item_id: row.master_item?.item_id,
          item_name: row.master_item?.item_name,
          item_code: row.master_item?.item_code,
          category_id: row.category?.category_id,
          category_name: row.category?.category_name,
          sub_category_id: row.sub_category?.sub_category_id,
          sub_category_name: row.sub_category?.sub_category_name,
          unit_id: row.unit?.unit_id,
          unit_name: row.unit?.unit_name,
          remark: row.remark || null,
          issued_quantity: Number(row.dispatch_quantity) || 0,
          master_item: row.master_item,
          // quantity: Number(row.dispatch_quantity) || 0,
          quantity: row.return_quantity || 0, // Use return_quantity as quantity
          bags: row.item_details?.bags || [],
          // Return items detail
          grn_items_details: [
            {
              quantity: Number(row.return_quantity) || 0,
              remark: row.remark || null,
            },
          ],

          // --- NEWLY ADDED FIELDS FROM PAYLOAD ---
          inventory_item_id: row.inventory_item_id, // inventory item reference
          bag_no: row.bag_no, // bag number
          dispatch_quantity: Number(row.dispatch_quantity) || 0, // dispatch quantity
          sr_no: row.sr_no, // serial number
          unit: row.unit, // original unit
        };
      });

      // Combine both arrays
      const finalItems = [...transformedItems, ...transformedReturnItems];

      // console.log("--------------------------------------------------");
      // finalItems.forEach((item, key) => {
      //   console.log(`${key} => ${key}`);
      // });
      // console.log("--------------------------------------------------");

      formData.append("grn_items", JSON.stringify(finalItems));
      return formData;
    },
    []
  );

  // Helper function to handle submit errors
  const handleSubmitError = useCallback((err: any) => {
    const responseData = err?.response?.data || err;
    const validationErrors = responseData?.data?.errors || responseData?.errors;

    if (Array.isArray(validationErrors) && validationErrors.length > 0) {
      try {
        showValidationErrors(validationErrors);
      } catch (swalError) {
        toast.error(validationErrors.join("; "));
      }
    } else {
      const errorMessage =
        responseData?.error ||
        responseData?.message ||
        "Failed to process GRN. Please try again.";
      toast.error(errorMessage);
    }
  }, []);

  const handleItemsUpdated = (items: any[]) => {
    setGrnItems(items);
  };

  const handleLabourItemsUpdated = (items: any[]) => {
    setLabourItems(items);
  };

  useEffect(() => {
    console.log("Updated GRN Items: ", grnItems);
  }, [grnItems]);

  useEffect(() => {
    console.log("Updated Labour Items: ", labourItems);
  }, [labourItems]);

  useEffect(() => {
    return () => {
      setLabourItems([]);
      dispatch(clearPendingLabourInwardDetails()); // Reset on unmount
    };
  }, []);

  return (
    <PageContainer>
      <FormHeader
        title={isEdit ? "Edit GRN(Labour)" : "Create Labour GRN"}
        firstLabel="GRN List"
         secondLabel={isEdit ? "Edit GRN(Labour)" : "Create Labour GRN"}
        handleArrowClick={() => navigate("/warehouse-item-inward")}
      />

      {loading || !initialDataLoaded ? (
        <LoadingSpinner />
      ) : (
        <Form autoComplete="off" onSubmit={formik.handleSubmit}>
          <Row>
            <Col lg={4}>
              <div className="mb-3">
                <Form.Label>
                  Inward Date <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  disabled
                  type="date"
                  name="inward_date"
                  value={formik.values.inward_date}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={
                    !!formik.errors.inward_date && formik.touched.inward_date
                  }
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.inward_date}
                </Form.Control.Feedback>
              </div>
            </Col>

            <Col lg={4}>
              <div className="mb-3">
                <Form.Label>
                  Dispatch No. <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  disabled
                  type="text"
                  name="dispatch_number"
                  // placeholder="Enter Dispatch Number"
                  value={formik.values.dispatch_number}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={
                    !!formik.errors.dispatch_number &&
                    formik.touched.dispatch_number
                  }
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.dispatch_id}
                </Form.Control.Feedback>
              </div>
            </Col>

            <Col lg={4}>
              <div className="mb-3">
                <Form.Label>
                  Labour Name <span className="text-danger">*</span>
                </Form.Label>

                <Autocomplete
                  disabled
                  options={labourOptions}
                  getOptionLabel={(option: any) => option?.labour_name || ""}
                  // ✅ Match by ID, not name
                  value={
                    labourOptions.find(
                      (opt: any) => opt.labour_id === formik.values.labour_id
                    ) || null
                  }
                  onChange={(event, selectedOption: any) => {
                    formik.setFieldValue(
                      "labour_id",
                      selectedOption?.labour_id || ""
                    );
                    formik.setFieldValue(
                      "labour_name",
                      selectedOption?.labour_name || ""
                    );
                    formik.setFieldTouched("labour_name", true);
                  }}
                  onBlur={() => formik.setFieldTouched("labour_name", true)}
                  renderInput={(params) => (
                    <BootstrapTextField
                      {...params}
                      placeholder="Select Labour"
                      name="labour_name"
                      error={
                        formik.touched.labour_name &&
                        Boolean(formik.errors.labour_name)
                      }
                      helperText={
                        formik.touched.labour_name && formik.errors.labour_name
                      }
                      fullWidth
                      size="small"
                      sx={{
                        background: "#FFF",
                        ...autocompleteDropdownStyles.inputSx,
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: "#ced4da",
                          },
                          "&:hover fieldset": {
                            borderColor: "#ced4da",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#ced4da",
                          },
                        },
                      }}
                    />
                  )}
                  renderOption={(props, option) =>
                    autocompleteDropdownStyles.renderOption(
                      props,
                      option,
                      (opt: any) => opt?.labour_name || ""
                    )
                  }
                  ListboxProps={autocompleteDropdownStyles.ListboxProps}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#ced4da",
                      },
                      "&:hover fieldset": {
                        borderColor: "#ced4da",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#ced4da",
                      },
                    },
                  }}
                />
              </div>
            </Col>

            <Col lg={4}>
              <div className="mb-3">
                <Form.Label>Remark</Form.Label>
                <Form.Control
                  type="text"
                  name="remark"
                  // placeholder="Enter Remark"
                  value={formik.values.remark}
                  onChange={formik.handleChange}
                />
              </div>
            </Col>

            {/* Upload Document */}
            <Col lg={3}>
              <div className="mb-3">
                <Form.Label>
                  Upload document
                  {/* <span className="text-danger">*</span> */}
                </Form.Label>

                {/* Custom styled box */}
                <div
                  className={`d-flex align-items-center justify-content-between p-2 border rounded ${
                    formik.errors.upload_document &&
                    formik.touched.upload_document
                      ? "border-danger"
                      : ""
                  }`}
                  style={{
                    height: "40px",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    backgroundColor: "#fff",
                    borderColor:
                      formik.errors.upload_document &&
                      formik.touched.upload_document
                        ? "#dc3545"
                        : "#ced4da",
                  }}
                  onMouseEnter={(e) => {
                    if (
                      !formik.errors.upload_document ||
                      !formik.touched.upload_document
                    ) {
                      e.currentTarget.style.borderColor = "#80bdff";
                      e.currentTarget.style.backgroundColor = "#f8f9fa";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (
                      !formik.errors.upload_document ||
                      !formik.touched.upload_document
                    ) {
                      e.currentTarget.style.borderColor = "#ced4da";
                      e.currentTarget.style.backgroundColor = "#fff";
                    }
                  }}
                  onClick={() => {
                    document.getElementById("uploadDocInput")?.click();
                  }}
                >
                  <span
                    className="text-muted"
                    style={{
                      flex: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      marginRight: "8px",
                      minWidth: 0,
                    }}
                    title={
                      formik.values.upload_document instanceof File
                        ? formik.values.upload_document.name
                        : formik.values.existing_document_file
                        ? formik.values.existing_document_file
                            .split("/")
                            .pop() || "Existing Document"
                        : "Click to upload document"
                    }
                  >
                    {formik.values.upload_document instanceof File ? (
                      formik.values.upload_document.name
                    ) : formik.values.existing_document_file ? (
                      <a
                        href={formik.values.existing_document_file}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-decoration-none text-primary"
                        style={{
                          cursor: "pointer",
                          display: "inline-block",
                          maxWidth: "100%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={
                          formik.values.existing_document_file
                            .split("/")
                            .pop() || "Existing Document"
                        }
                      >
                        {formik.values.existing_document_file
                          .split("/")
                          .pop() || "Existing Document"}
                      </a>
                    ) : (
                      "Click to upload document"
                    )}
                  </span>

                  {/* Action buttons */}
                  <div
                    className="d-flex align-items-center gap-2"
                    onClick={(e) => e.stopPropagation()}
                    style={{ flexShrink: 0 }}
                  >
                    {formik.values.upload_document instanceof File ? (
                      <>
                        <i
                          className="bi bi-upload text-primary"
                          style={{
                            cursor: "pointer",
                            fontSize: "1rem",
                            transition: "transform 0.2s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "scale(1.1)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "scale(1)";
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            document.getElementById("uploadDocInput")?.click();
                          }}
                          title="Change document"
                        ></i>
                        <i
                          className="bi bi-x-circle text-danger"
                          style={{
                            cursor: "pointer",
                            transition: "transform 0.2s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "scale(1.1)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "scale(1)";
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            formik.setFieldValue("upload_document", null);
                            formik.setFieldValue(
                              "existing_document_file",
                              null
                            );
                            // Reset file input
                            const fileInput = document.getElementById(
                              "uploadDocInput"
                            ) as HTMLInputElement;
                            if (fileInput) {
                              fileInput.value = "";
                            }
                          }}
                          title="Remove document"
                        ></i>
                      </>
                    ) : formik.values.existing_document_file  && formik.values.existing_document_file.trim() !== ""  ? (
                      <>
                        <a
                          href={formik.values.existing_document_file}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          title="View document"
                        >
                          <i
                            className="bi bi-eye text-primary"
                            style={{
                              cursor: "pointer",
                              fontSize: "1rem",
                              transition: "transform 0.2s ease",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = "scale(1.1)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = "scale(1)";
                            }}
                          ></i>
                        </a>
                        <i
                          className="bi bi-upload text-primary"
                          style={{
                            cursor: "pointer",
                            fontSize: "1rem",
                            transition: "transform 0.2s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "scale(1.1)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "scale(1)";
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            document.getElementById("uploadDocInput")?.click();
                          }}
                          title="Change document"
                        ></i>
                        <i
                          className="bi bi-x-circle text-danger"
                          style={{
                            cursor: "pointer",
                            transition: "transform 0.2s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "scale(1.1)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "scale(1)";
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            formik.setFieldValue("upload_document", null);
                            formik.setFieldValue(
                              "existing_document_file",
                              null
                            );
                            // Reset file input
                            const fileInput = document.getElementById(
                              "uploadDocInput"
                            ) as HTMLInputElement;
                            if (fileInput) {
                              fileInput.value = "";
                            }
                          }}
                          title="Remove document"
                        ></i>
                      </>
                    ) : (
                      <i
                        className="bi bi-upload"
                        style={{
                          cursor: "pointer",
                          fontSize: "1rem",
                          color: "#6c757d",
                          transition: "all 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = "#007bff";
                          e.currentTarget.style.transform = "scale(1.1)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = "#6c757d";
                          e.currentTarget.style.transform = "scale(1)";
                        }}
                      ></i>
                    )}
                  </div>
                </div>

                {/* Hidden real file input */}
                <Form.Control
                  id="uploadDocInput"
                  type="file"
                  name="upload_document"
                  style={{ display: "none" }}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    const file = event.currentTarget.files?.[0] || null;
                    formik.setFieldValue("upload_document", file);
                    // Clear existing_document_file when a new file is uploaded
                    if (file) {
                      formik.setFieldValue("existing_document_file", null);
                    }
                    formik.setFieldTouched("upload_document", true);
                  }}
                  onBlur={formik.handleBlur}
                  isInvalid={
                    !!formik.errors.upload_document &&
                    formik.touched.upload_document
                  }
                />

                <Form.Control.Feedback type="invalid">
                  {formik.errors.upload_document}
                </Form.Control.Feedback>
              </div>
            </Col>

            <Col lg={4}>
              <div className="mb-3">
                <Form.Label>
                  Received By <span className="text-danger">*</span>
                </Form.Label>
                <Autocomplete
                  options={userOptions}
                  getOptionLabel={(option: any) =>
                    option?.first_name && option?.last_name
                      ? `${option.first_name} ${option.last_name}`
                      : option?.username || option?.email || ""
                  }
                  value={
                    userOptions.find(
                      (opt: any) =>
                        opt.user_id === formik.values.received_by_user_id
                    ) || null
                  }
                  onChange={(event, selectedOption: any) => {
                    formik.setFieldValue(
                      "received_by_user_id",
                      selectedOption?.user_id || ""
                    );
                    formik.setFieldValue(
                      "received_by",
                      selectedOption?.first_name && selectedOption?.last_name
                        ? `${selectedOption.first_name} ${selectedOption.last_name}`
                        : selectedOption?.username || ""
                    );
                  }}
                  renderInput={(params) => (
                    <BootstrapTextField
                      {...params}
                      placeholder="Select User"
                      name="received_by"
                      error={
                        formik.touched.received_by &&
                        Boolean(formik.errors.received_by)
                      }
                      helperText={
                        formik.touched.received_by && formik.errors.received_by
                      }
                      fullWidth
                      size="small"
                      sx={{
                        background: "#FFF",
                        ...autocompleteDropdownStyles.inputSx,
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: "#ced4da",
                          },
                          "&:hover fieldset": {
                            borderColor: "#ced4da",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#ced4da",
                          },
                        },
                      }}
                    />
                  )}
                  renderOption={(props, option) =>
                    autocompleteDropdownStyles.renderOption(
                      props,
                      option,
                      (opt: any) =>
                        opt?.first_name && opt?.last_name
                          ? `${opt.first_name} ${opt.last_name}`
                          : opt?.username || opt?.email || ""
                    )
                  }
                  ListboxProps={autocompleteDropdownStyles.ListboxProps}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#ced4da",
                      },
                      "&:hover fieldset": {
                        borderColor: "#ced4da",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#ced4da",
                      },
                    },
                  }}
                />
              </div>
            </Col>
          </Row>

          <hr />

          <ConfigureItems
            inwardType={InwardType.LABOUR_INWARD}
            onItemsUpdated={handleItemsUpdated}
            initialItems={loading ? undefined : grnItems}
          />

          {labourItems.length > 0 && (
            <LabourReturnedItems
              onItemsUpdated={handleLabourItemsUpdated}
              initialItems={loading ? undefined : labourItems}
              isEdit={false}
            />
          )}

          <div className="d-flex justify-content-center gap-3 py-3">
            <CancelBackButton
              label="Cancel"
              onClick={() => {
                showConfirm(
                  "Any unsaved changes will be lost.",
                  "Are you sure?",
                  "Yes",
                  "No"
                ).then((result) => {
                  if (result.isConfirmed) {
                    dispatch(resetGRNState());
                    navigate("/warehouse-item-inward");
                  }
                });
              }}
            />

            <SubmitButton
              isSubmitting={formik.isSubmitting}
              labels={{
                create: "Update GRN",
                submitting: "Updating GRN",
              }}
            />
          </div>
        </Form>
      )}
    </PageContainer>
  );
};

export default EditLabourInwardGNR;
